import React, { useContext, useEffect, useRef, useState } from 'react'
import './Commands.css'
import { Button, Box, toast } from '@workwhile/ui'
import { Clipboard, ClipboardCheck } from 'lucide-react'

import Highlight from './Highlight'
import CommandField from './CommandField'
import { CommandFieldSchema, CommandSchema } from 'api/commands'
import { CommandContext } from './CommandProvider'
import { MarkdownRenderer } from 'components/MarkdownRenderer/MarkdownRenderer'

const getSortOrder = (
  a: Pick<CommandFieldSchema, 'type' | 'name' | 'multiple'>,
  b: Pick<CommandFieldSchema, 'type' | 'name' | 'multiple'>
): number => {
  // Checkboxes are last
  if (a.type === 'checkbox') {
    return 4
  }
  // Dates come after other types
  if (a.name.includes('date')) {
    return 3
  }
  // IDS come first, though
  if (!a.name.includes('_id') && b.name.includes('_id')) {
    return 2
  }
  // Generally, alphabetical order
  return a.name > b.name ? 1 : -1
}

const Command = ({ help, args, options, name, app_group }: CommandSchema) => {
  const { values, onSubmit } = useContext(CommandContext)
  const [result, setResult] = useState<{
    output: string
    exception: string | null
  } | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const outputContentRef = useRef<HTMLDivElement>(null)

  const handleSubmit = async () => {
    setResult(null)
    setIsExecuting(true)
    onSubmit(app_group, name)
      .then(setResult)
      .catch(setResult)
      .finally(() => setIsExecuting(false))
  }

  const commandText = [
    app_group,
    name,
    ...args.map((arg) => values[arg.name]),
    ...options.map((option) =>
      option.is_flag
        ? values[option.name]
          ? option?.opts?.[0]
          : option?.secondary_opts?.[0]
        : `--${option.name}=${values[option.name] ?? option.default}`
    ),
  ].join(' ')

  const outputText = isExecuting
    ? `Loading ${commandText} ...`
    : result?.exception
      ? result.exception
      : result?.output
        ? result.output
        : Object.values(values).length
          ? `Generated command: ${commandText}`
          : ''

  useEffect(() => {
    setIsCopied(false)
  }, [outputText])

  const handleCopyOutput = async () => {
    const textToCopy = outputContentRef.current?.innerText.trim() || outputText

    if (!textToCopy) {
      return
    }

    try {
      await navigator.clipboard.writeText(textToCopy)
      setIsCopied(true)
      toast.success('Copied command output to clipboard')
    } catch {
      toast.error('Failed to copy command output')
    }
  }

  return (
    <Box>
      {help ? (
        <Box mb="4" style={{ whiteSpace: 'pre-line' }}>
          <Highlight
            color="lightText"
            text={help.trim()}
            match={values.search}
          />
        </Box>
      ) : null}
      <Box mb="2">
        {[...args, ...options]
          ?.sort(getSortOrder)
          ?.map((field) => (
            <CommandField
              key={`${name}-${field.name}`}
              htmlFor={`${name}-${field.name}`}
              match={values.search}
              {...field}
            />
          ))}
      </Box>
      <Box>
        <Button loading={isExecuting} onClick={handleSubmit}>
          Execute
        </Button>

        <Box
          overflow="scroll"
          maxHeight="300px"
          maxWidth="800px"
          whiteSpace="pre-wrap"
          backgroundColor="#f8f8f8"
          border="1px solid #ccc"
          borderRadius="standard"
          borderWidth={1}
          mt={3}
          p={2}
          style={{ position: 'relative' }}
        >
          {outputText ? (
            <Box style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <Button
                variant="text"
                kind="small"
                title="Copy output"
                onClick={handleCopyOutput}
              >
                {isCopied ? (
                  <ClipboardCheck size={16} />
                ) : (
                  <Clipboard size={16} />
                )}
              </Button>
            </Box>
          ) : null}
          <div
            ref={outputContentRef}
            style={{ paddingRight: outputText ? 28 : undefined }}
          >
            {isExecuting ? (
              <MarkdownRenderer markdown={`Loading \`${commandText}\` ...`} />
            ) : result?.exception ? (
              <MarkdownRenderer markdown={result.exception} />
            ) : result?.output ? (
              <MarkdownRenderer markdown={result?.output || ''} />
            ) : Object.values(values).length ? (
              <MarkdownRenderer
                markdown={`Generated command: \`${commandText}\``}
              />
            ) : null}
          </div>
        </Box>
      </Box>
    </Box>
  )
}

export default Command
